import { useRef, useState, useEffect, useCallback } from "react";

/**
 * LogoDragCanvas
 * --------------
 * A miniature Letter-page representation with a draggable + resizable
 * logo inside. Emits position (percent 0..100 of page area) and size
 * (pixels, mapped from the on-screen canvas back to full-page px).
 *
 * Aspect ratio is always preserved on resize.
 *
 * Props:
 *   logoUrl:    string  — image to render inside the canvas
 *   freeX:      number  — 0..100 (%)
 *   freeY:      number  — 0..100 (%)
 *   logoHeight: number  — px, at page scale (matches design.layout.logo_size_px)
 *   onChange:   ({ freeX, freeY, logoHeight }) => void
 */
export default function LogoDragCanvas({ logoUrl, freeX, freeY, logoHeight, onChange }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [natRatio, setNatRatio] = useState(1);
  // Canvas represents an 8.5in x 11in Letter page. Preview height defaults
  // to ~380px; width follows automatically.
  const PREVIEW_H = 380;
  const PREVIEW_W = Math.round(PREVIEW_H * (8.5 / 11));
  // The actual document renders at ~850px total width for a Letter page.
  const PAGE_RENDER_W = 850;
  const scale = PREVIEW_W / PAGE_RENDER_W;

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const onImgLoaded = useCallback((e) => {
    const el = e.currentTarget;
    if (el.naturalWidth && el.naturalHeight) {
      setNatRatio(el.naturalWidth / el.naturalHeight);
    }
  }, []);

  // Drag handling ----------------------------------------------------------
  const startDrag = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const box = canvas.getBoundingClientRect();
    // Offset of grab point from the logo's origin (top-left).
    const logoLeftPx = (freeX / 100) * box.width;
    const logoTopPx = (freeY / 100) * box.height;
    const dxFromOrigin = e.clientX - box.left - logoLeftPx;
    const dyFromOrigin = e.clientY - box.top - logoTopPx;

    const onMove = (ev) => {
      const newLeftPx = clamp(ev.clientX - box.left - dxFromOrigin, 0, box.width);
      const newTopPx = clamp(ev.clientY - box.top - dyFromOrigin, 0, box.height);
      onChange({
        freeX: (newLeftPx / box.width) * 100,
        freeY: (newTopPx / box.height) * 100,
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Resize handling — aspect ratio preserved -------------------------------
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current;
    const box = canvas.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startHeight = logoHeight;
    const onMove = (ev) => {
      // Diagonal displacement determines new height. Convert canvas dx to
      // page-scale px using our fixed scale factor.
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      // Use the max of the two axes so the corner "chases" the pointer.
      const delta = Math.max(dx, dy);
      const newHeight = clamp(startHeight + delta / scale, 24, 320);
      onChange({ logoHeight: Math.round(newHeight) });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Snap-to-corner buttons -------------------------------------------------
  const snapTo = (anchor) => {
    const map = {
      "top-left": { freeX: 2, freeY: 2 },
      "top-center": { freeX: 42, freeY: 2 },
      "top-right": { freeX: 82, freeY: 2 },
      "middle-center": { freeX: 42, freeY: 45 },
      "bottom-left": { freeX: 2, freeY: 90 },
      "bottom-center": { freeX: 42, freeY: 90 },
      "bottom-right": { freeX: 82, freeY: 90 },
    };
    if (map[anchor]) onChange(map[anchor]);
  };

  // Compute visible logo dims (canvas coords) -----------------------------
  const logoPreviewHeightPx = (logoHeight || 64) * scale;
  const logoPreviewWidthPx = logoPreviewHeightPx * (natRatio || 1);

  return (
    <div className="space-y-2">
      <div
        ref={canvasRef}
        className="relative bg-white border border-zinc-300 rounded-md mx-auto overflow-hidden select-none"
        style={{ width: PREVIEW_W, height: PREVIEW_H }}
        data-testid="logo-drag-canvas"
      >
        {/* Faint grid to show it's a page */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "linear-gradient(#f4f4f5 1px, transparent 1px), linear-gradient(90deg, #f4f4f5 1px, transparent 1px)",
          backgroundSize: `${PREVIEW_W / 8}px ${PREVIEW_H / 11}px`,
          opacity: 0.6,
        }}/>
        {/* Subtle page-title placeholder */}
        <div className="absolute left-3 right-3 top-3 text-[9px] uppercase tracking-widest text-zinc-300 font-mono">Page canvas · Letter</div>

        {logoUrl ? (
          <div
            className="absolute cursor-move ring-1 ring-transparent hover:ring-orange-400 group"
            style={{
              left: `${freeX ?? 0}%`,
              top: `${freeY ?? 0}%`,
              width: logoPreviewWidthPx,
              height: logoPreviewHeightPx,
            }}
            onMouseDown={startDrag}
            data-testid="logo-drag-handle"
          >
            <img
              ref={imgRef}
              src={logoUrl}
              alt=""
              onLoad={onImgLoaded}
              className="w-full h-full object-contain pointer-events-none"
              draggable={false}
            />
            {/* Resize handle (bottom-right corner) */}
            <div
              className="absolute -right-1 -bottom-1 w-3 h-3 bg-[#FF5A00] rounded-sm cursor-nwse-resize ring-2 ring-white opacity-80 group-hover:opacity-100"
              onMouseDown={startResize}
              data-testid="logo-resize-handle"
            />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-400">
            Upload a logo to enable drag positioning
          </div>
        )}
      </div>

      {/* Snap-to-anchor buttons */}
      {logoUrl && (
        <div className="grid grid-cols-3 gap-1" data-testid="logo-snap-controls">
          {[
            ["top-left", "↖"],
            ["top-center", "↑"],
            ["top-right", "↗"],
            ["middle-center", "◎"],
            ["bottom-left", "↙"],
            ["bottom-center", "↓"],
            ["bottom-right", "↘"],
          ].map(([k, glyph]) => (
            <button
              key={k}
              onClick={() => snapTo(k)}
              className="text-xs px-2 py-1.5 bg-white border border-zinc-200 rounded hover:border-[#FF5A00] hover:text-[#FF5A00] transition"
              data-testid={`logo-snap-${k}`}
              type="button"
              title={`Snap to ${k.replace("-", " ")}`}
            >
              {glyph}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
